// Message search: a side panel like a list's (it takes the thread panel's place), searching as you type. Words have
// to be in the message; Discord-style filters narrow it (from:, mentions:, has:, in:, before:, after:, during:,
// pinned:; parsed by shared/src/search.ts, applied by the room). Typing suggests filters and their values; understood
// filters show as chips under the box (× takes one out). Newest first; Jump takes you to the message. Searches the
// whole server by default, or just the open channel (and its threads).
import { useEffect, useMemo, useRef, useState } from "react";
import {
  parseSearch,
  SEARCH_HAS,
  SEARCH_KEY_HINTS,
  SEARCH_KEYS,
  searchHasQuery,
  searchValue,
  type Message,
  type SearchFilters,
  type SearchHit,
  type SearchKey,
} from "@bloom/shared";
import { Avatar } from "./ui.tsx";
import { CloseButton } from "./icons.tsx";
import "./lists.css";
import { Markdown } from "./Markdown.tsx";
import { Attachments } from "./Attachments.tsx";

export interface SearchSource {
  search(q: string, opts?: { channelId?: string; before?: number; filters?: SearchFilters }): Promise<{ hits: SearchHit[]; messages: Message[]; more: boolean } | { error: string }>;
}

/** One suggestion under the box: what it shows, and what replaces the bit being typed. */
interface Suggestion {
  key: string;
  label: string;
  hint?: string;
  insert: string;
  /** A person (for their avatar). */
  personId?: string;
}

/** The `key:value` (or plain word) being typed at the caret: where it starts, and its text. */
function typingAt(q: string, caret: number): { start: number; text: string } {
  const before = q.slice(0, caret);
  const m = /(?:^|\s)([a-z]+:"[^"]*|\S*)$/i.exec(before);
  const text = m?.[1] ?? "";
  return { start: caret - text.length, text };
}

export function SearchPanel({
  source,
  names,
  people,
  channels,
  me,
  channel,
  where,
  plain,
  onJump,
  onClose,
  panelRef,
}: {
  source: SearchSource;
  names: Map<string, string>;
  /** People from: and mentions: can name (the server's members, bots and Bloom). */
  people: { id: string; name: string }[];
  /** Text channels in: can name. */
  channels: { id: string; name: string }[];
  me: string | null;
  /** The open channel or DM, to offer "only here": its id and how it's called ("#general", "Maya"). */
  channel: { id: string; label: string } | null;
  where: (stream: string, rootId: string) => string;
  plain: (text: string) => string;
  onJump: (hit: SearchHit) => void;
  onClose: () => void;
  panelRef?: React.Ref<HTMLElement>;
}) {
  const [q, setQ] = useState("");
  const [caret, setCaret] = useState(0);
  const [focused, setFocused] = useState(true);
  const [active, setActive] = useState(0);
  const [here, setHere] = useState(false);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [messages, setMessages] = useState<Map<string, Message>>(new Map());
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState<{ words: string; filtered: boolean } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const scope = here && channel ? channel.id : undefined;
  /** Bumped on every new search, so an older answer arriving late is dropped. */
  const ticket = useRef(0);

  const parsed = useMemo(() => parseSearch(q, { people, channels, me }), [q, people, channels, me]);
  // The filter being typed (the caret in it) isn't wrong yet, just unfinished: no error for it, and no search.
  const typingToken = (t: { start: number; end: number }) => focused && caret >= t.start && caret <= t.end;
  const problem = parsed.tokens.find((t) => t.error && !typingToken(t))?.error ?? null;
  const ready = !parsed.tokens.some((t) => t.error) && searchHasQuery(parsed);
  /** What's sent: the words and filters, as one key so the search runs again only when they change. */
  const request = ready ? JSON.stringify({ w: parsed.words, f: parsed.filters }) : "";

  useEffect(() => input.current?.focus(), []);

  useEffect(() => {
    const mine = ++ticket.current;
    if (!request) {
      setHits([]);
      setMessages(new Map());
      setMore(false);
      setError(null);
      setSearched(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const words = parsed.words.join(" ");
    const filters = parsed.filters;
    const t = setTimeout(async () => {
      const r = await source.search(words, { channelId: scope, filters });
      if (mine !== ticket.current) return;
      setLoading(false);
      if ("error" in r) return setError(r.error);
      setError(null);
      setHits(r.hits);
      setMessages(new Map(r.messages.map((m) => [m.id, m])));
      setMore(r.more);
      setSearched({ words, filtered: parsed.tokens.length > 0 });
    }, 250);
    return () => clearTimeout(t);
  }, [request, scope]);

  const loadMore = async () => {
    if (!more || loading || !searched || !request) return;
    const last = messages.get(hits.at(-1)?.messageId ?? "");
    if (!last) return;
    const mine = ticket.current;
    setLoading(true);
    const r = await source.search(parsed.words.join(" "), { channelId: scope, before: last.ts, filters: parsed.filters });
    if (mine !== ticket.current) return;
    setLoading(false);
    if ("error" in r) return setError(r.error);
    setHits((x) => [...x, ...r.hits.filter((h) => !x.some((y) => y.messageId === h.messageId))]);
    setMessages((x) => new Map([...x, ...r.messages.map((m) => [m.id, m] as const)]));
    setMore(r.more);
  };

  // ---- Suggestions: filters while typing a word, then the values of the one being typed ----
  const typing = typingAt(q, caret);
  const suggestions: Suggestion[] = useMemo(() => {
    const t = typing.text;
    const colon = t.indexOf(":");
    if (colon < 0) {
      const lower = t.toLowerCase();
      return SEARCH_KEYS.filter((k) => k.startsWith(lower) && (lower.length > 0 || q.trim() === "" || /\s$/.test(q.slice(0, caret)))).map((k) => ({
        key: k,
        label: `${k}:`,
        hint: SEARCH_KEY_HINTS[k],
        insert: `${k}:`,
      }));
    }
    const key = t.slice(0, colon).toLowerCase() as SearchKey;
    if (!(SEARCH_KEYS as readonly string[]).includes(key)) return [];
    const typed = t.slice(colon + 1).replace(/^"/, "").replace(/"$/, "").toLowerCase();
    const value = (v: string, label = v, extra: Partial<Suggestion> = {}): Suggestion => ({ key: `${key}:${v}`, label, insert: `${key}:${searchValue(v)} `, ...extra });
    switch (key) {
      case "from":
      case "mentions":
        return people
          .filter((p) => p.name.toLowerCase().includes(typed.replace(/^@/, "")))
          .sort((a, b) => Number(!a.name.toLowerCase().startsWith(typed)) - Number(!b.name.toLowerCase().startsWith(typed)) || a.name.localeCompare(b.name))
          .slice(0, 8)
          .map((p) => value(p.name, p.name, { personId: p.id }));
      case "in":
        return channels
          .filter((c) => c.name.toLowerCase().includes(typed.replace(/^#/, "")))
          .slice(0, 8)
          .map((c) => value(c.name, `#${c.name}`));
      case "has":
        return SEARCH_HAS.filter((h) => h.startsWith(typed)).map((h) => value(h));
      case "before":
      case "after":
      case "during": {
        const today = new Date();
        const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return ["today", "yesterday", iso(today)].filter((v) => v.startsWith(typed)).map((v) => value(v, v, { hint: v === iso(today) ? "or any date like this" : undefined }));
      }
      case "pinned":
        return "true".startsWith(typed) ? [value("true", "true", { hint: "Only pinned messages" })] : [];
    }
  }, [typing.text, typing.start, people, channels, q, caret]);
  // Nothing to suggest once a value is complete and followed by a space, or when the box isn't focused.
  const showSuggestions = focused && suggestions.length > 0;
  useEffect(() => setActive(0), [typing.text]);

  const choose = (s: Suggestion) => {
    const next = q.slice(0, typing.start) + s.insert + q.slice(caret).replace(/^\S*/, "");
    const at = typing.start + s.insert.length;
    setQ(next);
    setCaret(at);
    requestAnimationFrame(() => {
      input.current?.focus();
      input.current?.setSelectionRange(at, at);
    });
  };
  const removeToken = (start: number, end: number) => {
    const next = (q.slice(0, start) + q.slice(end)).replace(/\s{2,}/g, " ").trimStart();
    setQ(next);
    setCaret(next.length);
    input.current?.focus();
  };

  const noun = here && channel ? ` in ${channel.label}` : "";
  return (
    <aside className="list-panel search-panel" aria-label="Search" ref={panelRef}>
      <div className="thread-head list-head">
        <button className="icon-btn mobile-only head-btn" onClick={onClose} aria-label="Back to chat">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <form className="search-field" role="search" onSubmit={(e) => e.preventDefault()}>
          <SearchIcon size={16} />
          <label htmlFor="search-q" className="sr-only">Search messages</label>
          <input
            id="search-q"
            ref={input}
            className="search-input"
            type="search"
            placeholder={here && channel ? `Search ${channel.label}` : "Search messages"}
            value={q}
            enterKeyHint="search"
            autoComplete="off"
            role="combobox"
            aria-expanded={showSuggestions}
            aria-controls="search-suggest"
            aria-activedescendant={showSuggestions ? `search-suggest-${active}` : undefined}
            onChange={(e) => {
              setQ(e.target.value);
              setCaret(e.target.selectionStart ?? e.target.value.length);
            }}
            onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? q.length)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={(e) => {
              if (showSuggestions && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
                e.preventDefault();
                const n = suggestions.length;
                setActive((i) => (i + (e.key === "ArrowDown" ? 1 : n - 1)) % n);
                return;
              }
              if (showSuggestions && (e.key === "Enter" || e.key === "Tab") && suggestions[active]) {
                e.preventDefault();
                choose(suggestions[active]);
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                if (showSuggestions) setFocused(false);
                else if (q) setQ("");
                else onClose();
              }
            }}
          />
        </form>
        <CloseButton className="desktop-only" onClick={onClose} aria-label="Close search" />
      </div>
      {showSuggestions && (
        <div className="search-suggest" id="search-suggest" role="listbox" aria-label="Search suggestions">
          {suggestions.map((s, i) => (
            <button
              key={s.key}
              id={`search-suggest-${i}`}
              type="button"
              role="option"
              aria-selected={i === active}
              className={`search-suggest-item${i === active ? " active" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(s)}
            >
              {s.personId && <Avatar id={s.personId} name={s.label} size={20} />}
              <span className="search-suggest-label">{s.label}</span>
              {s.hint && <span className="search-suggest-hint muted">{s.hint}</span>}
            </button>
          ))}
        </div>
      )}
      {parsed.tokens.some((t) => !(t.error && typingToken(t))) && (
        <div className="search-chips" aria-label="Filters">
          {parsed.tokens.filter((t) => !(t.error && typingToken(t))).map((t) => (
            <span key={`${t.start}:${t.key}`} className={`search-chip${t.error ? " bad" : ""}`} title={t.error}>
              <span>{t.error ? `${t.key}:${t.value}` : t.label}</span>
              <button type="button" aria-label={`Remove ${t.error ? `${t.key}:${t.value}` : t.label}`} onClick={() => removeToken(t.start, t.end)}>×</button>
            </span>
          ))}
        </div>
      )}
      {channel && (
        <div className="search-scope" role="radiogroup" aria-label="Where to search">
          <button type="button" role="radio" aria-checked={!here} className={`chip${!here ? " on" : ""}`} onClick={() => setHere(false)}>Everywhere</button>
          <button type="button" role="radio" aria-checked={here} className={`chip${here ? " on" : ""}`} onClick={() => setHere(true)}>In {channel.label}</button>
        </div>
      )}
      <div
        className="list-items"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) void loadMore();
        }}
      >
        {(error || problem) && <div className="list-empty error" role="alert">{problem ?? error}</div>}
        {!error && !problem && !q.trim() && (
          <div className="list-empty search-help">
            <div className="list-empty-glyph"><SearchIcon size={28} /></div>
            <p>Find messages by any words in them. Every word has to be there.</p>
            <p className="muted">
              Narrow it with <code>from:</code>, <code>mentions:</code>, <code>has:image</code> (or link, file, video, GIF, sticker, embed), <code>in:#channel</code>,{" "}
              <code>before:</code>/<code>after:</code>/<code>during:</code> a date, or <code>pinned:true</code>.
            </p>
          </div>
        )}
        {!error && !problem && !loading && !!searched && !hits.length && (
          <div className="list-empty">
            <p>{searched.words ? `No messages with “${searched.words}”` : "No messages match"}{searched.filtered && searched.words ? " and those filters" : searched.filtered ? " those filters" : ""}{noun}.</p>
          </div>
        )}
        {!!searched && !!hits.length && (
          <div className="search-count muted" role="status">
            {hits.length}{more ? "+" : ""} {hits.length === 1 && !more ? "result" : "results"}
          </div>
        )}
        {hits.map((h) => {
          const m = messages.get(h.messageId);
          if (!m) return null;
          const author = names.get(m.authorId) ?? (m.authorId === "bloom" ? "Bloom" : "Someone");
          return (
            <article key={h.messageId} className="list-item search-hit" data-mid={h.messageId}>
              <div className="li-head">
                <Avatar id={m.authorId} name={author} size={22} />
                <strong>{author}</strong>
                <span className="muted li-where">{where(h.channelId, h.rootId)}</span>
                <time className="muted" dateTime={new Date(m.ts).toISOString()}>{dateFmt.format(m.ts)}</time>
              </div>
              {m.body && (
                <div className="li-body body">
                  <Markdown source={m.body} text={(t) => plain(t)} />
                </div>
              )}
              {m.attachments && <Attachments messageId={m.id} attachments={m.attachments} onOpen={(src) => window.open(src, "_blank", "noopener")} />}
              <div className="li-actions">
                <button type="button" className="btn btn-small" onClick={() => onJump(h)}>Jump</button>
              </div>
            </article>
          );
        })}
        {loading && <div className="list-empty muted">Searching…</div>}
      </div>
    </aside>
  );
}

const dateFmt = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function SearchIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}
